/**
 * SourceBadgesV2 Components (EVI-721)
 *
 * SourceModeIcons — inline mode icons rendered INSIDE the Sources button
 * (+ pro trial ⚡ indicator).
 */

import { TierProFilled } from '@heidi/design-system/icons';
import { sourceControlToggleAtom } from '@/atoms/sourceControlAtoms';;
import { useAvailableIntegrations } from '@/components/chat/hooks/useAvailableIntegrations';
import {
    Tooltip,
    TooltipArrow,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtomValue } from 'jotai';
import { BadgeCheck, BookOpen, LibraryBig, Stethoscope, type LucideIcon } from 'lucide-react';

export const SourceModeIcons = () => {
  const { t } = useTranslations();
  const toggleState = useAtomValue(sourceControlToggleAtom);
  const { isInFreeTrial } = useEvidenceBillingInfo();
  const { partners: availablePartners } = useAvailableIntegrations();

  const isWebSearchActive = toggleState.webSearch;
  const isClinicalSourcesActive =
    toggleState.localGuidelines || toggleState.primaryEvidence;
  const isMyLibraryActive =
    toggleState.myLibrary || toggleState.webCollections;
  const isClinicalToolsActive = availablePartners.some(
    (p) => toggleState.integrationPartners[p.id] === true
  );

  const hasAnyMode =
    isWebSearchActive ||
    isClinicalSourcesActive ||
    isMyLibraryActive ||
    isClinicalToolsActive;

  if (!hasAnyMode && !isInFreeTrial) {
    return null;
  }

  return (
    <TooltipProvider>
      <span className="inline-flex items-center gap-1">
        {/* Pro trial indicator */}
        {isInFreeTrial && (
          <TierProFilled className="size-3 shrink-0 text-tier-pro" />
        )}

        {/* Mode icons */}
        {isWebSearchActive && (
          <ModeIcon icon={Stethoscope} tooltip={t('sources.sourceControl.webSearch')} />
        )}
        {isClinicalSourcesActive && (
          <ModeIcon icon={BookOpen} tooltip={t('sources.sourceControl.clinicalSources')} />
        )}
        {isMyLibraryActive && (
          <ModeIcon icon={LibraryBig} tooltip={t('sources.sourceControl.myLibrary')} />
        )}
        {isClinicalToolsActive && (
          <ModeIcon icon={BadgeCheck} tooltip={t('sources.sourceControl.clinicalTools')} />
        )}
      </span>
    </TooltipProvider>
  );
};

/** Small inline mode icon with tooltip */
const ModeIcon = ({
  icon: Icon,
  tooltip,
}: {
  icon: LucideIcon;
  tooltip: string;
}) => (
  <Tooltip delayDuration={300}>
    <TooltipTrigger asChild>
      <span className="inline-flex items-center justify-center text-current">
        <Icon size={14} />
      </span>
    </TooltipTrigger>
    <TooltipContent side="top">
      {tooltip}
      <TooltipArrow />
    </TooltipContent>
  </Tooltip>
);
